import { LysepAPI } from '../contexts/ApiContext';

/**
 * Blog API Service
 * Handles all blog-related API calls
 */

// ============================================
// POSTS
// ============================================

/**
 * Get all posts (public or admin)
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getPosts = async (params = {}) => {
  const response = await LysepAPI.get('/api/blog/posts', { params });
  return response.data;
};

/**
 * Get all posts (admin only - includes draft, published, archived)
 * @param {Object} params - Query parameters (limit, offset, etc.)
 * @returns {Promise}
 */
export const getAdminPosts = async (params = {}) => {
  const response = await LysepAPI.get('/api/blog/admin/posts', { params });
  return response.data;
};

/**
 * Get single post by ID (admin only - includes all statuses)
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const getAdminPost = async (id) => {
  const response = await LysepAPI.get(`/api/blog/admin/posts/${id}`);
  return response.data;
};

/**
 * Get single post by ID (public)
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const getPost = async (id) => {
  const response = await LysepAPI.get(`/api/blog/posts/${id}`);
  return response.data;
};

/**
 * Get post by slug
 * @param {string} slug - Post slug
 * @returns {Promise}
 */
export const getPostBySlug = async (slug) => {
  const response = await LysepAPI.get(`/api/blog/posts/slug/${slug}`);
  return response.data;
};

/**
 * Create new post
 * @param {Object} postData - Post data
 * @returns {Promise}
 */
export const createPost = async (postData) => {
  const response = await LysepAPI.post('/api/blog/posts', postData);
  return response.data;
};

/**
 * Update post
 * @param {number} id - Post ID
 * @param {Object} postData - Post data
 * @returns {Promise}
 */
export const updatePost = async (id, postData) => {
  const response = await LysepAPI.put(`/api/blog/posts/${id}`, postData);
  return response.data;
};

/**
 * Delete post
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const deletePost = async (id) => {
  const response = await LysepAPI.delete(`/api/blog/posts/${id}`);
  return response.data;
};

/**
 * Publish post
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const publishPost = async (id) => {
  const response = await LysepAPI.post(`/api/blog/posts/${id}/publish`);
  return response.data;
};

/**
 * Unpublish post
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const unpublishPost = async (id) => {
  const response = await LysepAPI.post(`/api/blog/posts/${id}/unpublish`);
  return response.data;
};

/**
 * Get post translations
 * @param {number} id - Post ID
 * @returns {Promise}
 */
export const getPostTranslations = async (id) => {
  const response = await LysepAPI.get(`/api/blog/posts/${id}/translations`);
  return response.data;
};

/**
 * Add/Update post translation
 * @param {number} id - Post ID
 * @param {Object} translationData - Translation data
 * @returns {Promise}
 */
export const addPostTranslation = async (id, translationData) => {
  const response = await LysepAPI.post(`/api/blog/posts/${id}/translations`, translationData);
  return response.data;
};

/**
 * Update post translation
 * @param {number} id - Post ID
 * @param {string} lang - Language code
 * @param {Object} translationData - Translation data
 * @returns {Promise}
 */
export const updatePostTranslation = async (id, lang, translationData) => {
  const response = await LysepAPI.put(`/api/blog/posts/${id}/translations/${lang}`, translationData);
  return response.data;
};

/**
 * Delete post translation
 * @param {number} id - Post ID
 * @param {string} lang - Language code
 * @returns {Promise}
 */
export const deletePostTranslation = async (id, lang) => {
  const response = await LysepAPI.delete(`/api/blog/posts/${id}/translations/${lang}`);
  return response.data;
};

// ============================================
// CATEGORIES
// ============================================

/**
 * Get all categories
 * @param {Object} params - Query parameters (tree=true for tree structure)
 * @returns {Promise}
 */
export const getCategories = async (params = {}) => {
  const response = await LysepAPI.get('/api/blog/categories', { params });
  return response.data;
};

/**
 * Get single category
 * @param {number} id - Category ID
 * @returns {Promise}
 */
export const getCategory = async (id) => {
  const response = await LysepAPI.get(`/api/blog/categories/${id}`);
  return response.data;
};

/**
 * Get posts in category
 * @param {number} id - Category ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getCategoryPosts = async (id, params = {}) => {
  const response = await LysepAPI.get(`/api/blog/categories/${id}/posts`, { params });
  return response.data;
};

/**
 * Create category
 * @param {Object} categoryData - Category data
 * @returns {Promise}
 */
export const createCategory = async (categoryData) => {
  const response = await LysepAPI.post('/api/blog/categories', categoryData);
  return response.data;
};

/**
 * Update category
 * @param {number} id - Category ID
 * @param {Object} categoryData - Category data
 * @returns {Promise}
 */
export const updateCategory = async (id, categoryData) => {
  const response = await LysepAPI.put(`/api/blog/categories/${id}`, categoryData);
  return response.data;
};

/**
 * Delete category
 * @param {number} id - Category ID
 * @returns {Promise}
 */
export const deleteCategory = async (id) => {
  const response = await LysepAPI.delete(`/api/blog/categories/${id}`);
  return response.data;
};

// ============================================
// TAGS
// ============================================

/**
 * Get all tags
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getTags = async (params = {}) => {
  const response = await LysepAPI.get('/api/blog/tags', { params });
  return response.data;
};

/**
 * Get single tag
 * @param {number} id - Tag ID
 * @returns {Promise}
 */
export const getTag = async (id) => {
  const response = await LysepAPI.get(`/api/blog/tags/${id}`);
  return response.data;
};

/**
 * Get posts with tag
 * @param {number} id - Tag ID
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getTagPosts = async (id, params = {}) => {
  const response = await LysepAPI.get(`/api/blog/tags/${id}/posts`, { params });
  return response.data;
};

/**
 * Create tag
 * @param {Object} tagData - Tag data
 * @returns {Promise}
 */
export const createTag = async (tagData) => {
  const response = await LysepAPI.post('/api/blog/tags', tagData);
  return response.data;
};

/**
 * Update tag
 * @param {number} id - Tag ID
 * @param {Object} tagData - Tag data
 * @returns {Promise}
 */
export const updateTag = async (id, tagData) => {
  const response = await LysepAPI.put(`/api/blog/tags/${id}`, tagData);
  return response.data;
};

/**
 * Delete tag
 * @param {number} id - Tag ID
 * @returns {Promise}
 */
export const deleteTag = async (id) => {
  const response = await LysepAPI.delete(`/api/blog/tags/${id}`);
  return response.data;
};

